import {useAnalytics} from '@github-ui/use-analytics'
import {FilterIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {useState, useMemo} from 'react'
import styles from '../DashboardLists.module.css'
import type {MenuItem, QueryQualifier, AgentQueryQualifier, MaxAgeQualifier, MaxAgeMenuItem} from '../types'
import {RESULT_COUNTS} from '../constants'

export interface ListActionMenuProps<TQualifier extends QueryQualifier | AgentQueryQualifier> {
  setQueryQualifiers: (queries: TQualifier[]) => void
  queryQualifiers: TQualifier[]
  setResultCount: (count: number) => void
  resultCount: number
  menuItems: Array<MenuItem<TQualifier>>
  label: string
  selectedMaxAgeQualifier?: MaxAgeQualifier
  setMaxAgeQualifier?: (qualifier: MaxAgeQualifier) => void
  maxAgeMenuItems?: Array<MaxAgeMenuItem<MaxAgeQualifier>>
  filterGroupHeading?: string
  analyticsEventType?: string
  analyticsTarget?: string
  analyticsCategory?: string
}

export function ListActionMenu<TQualifier extends QueryQualifier | AgentQueryQualifier>({
  setQueryQualifiers,
  queryQualifiers,
  setResultCount,
  resultCount,
  menuItems,
  label,
  selectedMaxAgeQualifier,
  setMaxAgeQualifier,
  maxAgeMenuItems,
  filterGroupHeading,
  analyticsEventType,
  analyticsTarget,
  analyticsCategory,
}: ListActionMenuProps<TQualifier>) {
  const selectedIndices = useMemo(
    () => new Set(queryQualifiers.map(qualifier => menuItems.findIndex(item => item.value === qualifier))),
    [queryQualifiers, menuItems],
  )
  const isValidMenuItemIndex = (index: number): boolean => index >= 0 && index < menuItems.length
  const getQueryQualifiers = (indices: Set<number>): TQualifier[] => {
    return Array.from(indices).reduce<TQualifier[]>((acc, selectedIndex) => {
      if (isValidMenuItemIndex(selectedIndex)) {
        const value = menuItems[selectedIndex]?.value
        if (value !== undefined) acc.push(value)
      }
      return acc
    }, [])
  }

  const [open, setOpen] = useState<boolean>(false)
  const {sendAnalyticsEvent} = useAnalytics()

  const handleSelect = (index: number): void => {
    const newSelectedIndices = new Set(selectedIndices)
    if (selectedIndices.has(index)) {
      newSelectedIndices.delete(index)
    } else {
      newSelectedIndices.add(index)
    }

    setQueryQualifiers(getQueryQualifiers(newSelectedIndices))
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)

    if (!isOpen && analyticsEventType && analyticsTarget && analyticsCategory) {
      sendAnalyticsEvent(analyticsEventType, analyticsTarget, {
        filters: queryQualifiers.join(','),
        category: analyticsCategory,
        result_count: resultCount.toString(),
        max_age: selectedMaxAgeQualifier,
      })
    }
  }

  return (
    <ActionMenu open={open} onOpenChange={handleOpenChange}>
      <ActionMenu.Anchor>
        <IconButton
          icon={FilterIcon}
          variant="invisible"
          aria-label={label}
          tooltipDirection="n"
          className={styles.ActionMenuButton}
          size="small"
          data-testid="action-menu-button"
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="small" align="end" data-testid="action-menu-overlay">
        <ActionList role="menu">
          {filterGroupHeading && menuItems.length > 0 ? (
            <>
              <ActionList.Group selectionVariant="multiple">
                <ActionList.GroupHeading>{filterGroupHeading}</ActionList.GroupHeading>
                {menuItems.map((item, index) => (
                  <ActionList.Item
                    key={item.value}
                    role="menuitemcheckbox"
                    selected={selectedIndices.has(index)}
                    aria-checked={selectedIndices.has(index)}
                    onSelect={e => {
                      e.preventDefault() // Prevent ActionMenu from closing on select
                      handleSelect(index)
                    }}
                  >
                    {item.label}
                  </ActionList.Item>
                ))}
              </ActionList.Group>
              <ActionList.Divider />
            </>
          ) : null}

          <ActionList.Group selectionVariant="single">
            <ActionList.GroupHeading>Number of results</ActionList.GroupHeading>
            {RESULT_COUNTS.map(count => (
              <ActionList.Item
                key={count}
                role="menuitemradio"
                selected={resultCount === count}
                aria-checked={resultCount === count}
                onSelect={e => {
                  e.preventDefault() // Prevent ActionMenu from closing on select
                  setResultCount(count)
                }}
              >
                {count}
              </ActionList.Item>
            ))}
          </ActionList.Group>

          {maxAgeMenuItems && setMaxAgeQualifier ? (
            <>
              <ActionList.Divider />
              <ActionList.Group selectionVariant="single">
                <ActionList.GroupHeading>Maximum age of results</ActionList.GroupHeading>
                {maxAgeMenuItems.map(item => (
                  <ActionList.Item
                    key={item.value}
                    role="menuitemradio"
                    selected={selectedMaxAgeQualifier === item.value}
                    aria-checked={selectedMaxAgeQualifier === item.value}
                    onSelect={e => {
                      e.preventDefault() // Prevent ActionMenu from closing on select
                      setMaxAgeQualifier(item.value)
                    }}
                  >
                    {item.label}
                  </ActionList.Item>
                ))}
              </ActionList.Group>
            </>
          ) : null}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
